import { useCallback, useState } from 'react'
import { postWithToken } from './http'

export interface APIKeyCreateRequest {
  organizationId: string
  name: string
  permissions: string[]
}

export interface APIKeyResponse {
  id: string
  organizationId: string
  name: string
  permissions: string[]
  lastUsed?: string
  createdAt: string
  updatedAt: string
  isActive: boolean
}

export interface APIKeyCreateResponse extends APIKeyResponse {
  plainKey: string
}

interface UseAPIKeysReturn {
  createAPIKey: (request: APIKeyCreateRequest) => Promise<APIKeyCreateResponse>
  isLoading: boolean
  error: Error | null
}

export const useAPIKeys = (token: string): UseAPIKeysReturn => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createAPIKey = useCallback(
    async (request: APIKeyCreateRequest): Promise<APIKeyCreateResponse> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await postWithToken('api-keys', token, request)
        return response as APIKeyCreateResponse
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create API key')
        setError(error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return {
    createAPIKey,
    isLoading,
    error,
  }
}
